/**
 * /service-prices (OCP-851) - the public service price list ("Preisliste für
 * Servicearbeiten") AGB V4 6.6 refers to. No session needed: the order
 * summary, the order confirmation mail and the legal links point here.
 * Mirrors /terms: `?version=<label>` serves exactly that published version
 * (what an order names), `?lang=` the language; without `version` the list in
 * force. Net prices, B2B. With nothing published the page says so (6.6 is
 * then not applied).
 *
 * The text around the table comes from the frozen `content_md`; the table is
 * rendered from the same version's structured items (the portal renders
 * Markdown without table support), so screen and fingerprint stay one source.
 */
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { OrbotoLockup } from '../components/OrbotoLockup';
import { api, ApiError } from '../lib/api';
import { LOCALE_LABELS, currentLocale } from '../i18n';
import { LegalFooter } from '../components/LegalFooter';

export interface PublicServicePriceVersion {
  version: string;
  title: string;
  language: 'de' | 'en';
  effective_from: string;
  published_at: string;
  content_md: string;
  content_sha256: string;
  items: Array<{ name: string; unit: string; unit_label: string; net_amount_cents: number; note: string | null }>;
  note: string | null;
  current: boolean;
  scheduled: boolean;
}

interface HistoryEntry {
  version: string;
  /** `withdrawn` = a scheduled version taken back before it took effect; listed, never served. */
  status: 'published' | 'withdrawn';
  effective_from: string;
  current: boolean;
  scheduled: boolean;
}

/** The link an order summary / legal link uses for one version. */
export function servicePricesHref(version?: string | null, lang?: string): string {
  const q = new URLSearchParams({ ...(version ? { version } : {}), ...(lang ? { lang } : {}) });
  const s = q.toString();
  return `/service-prices${s ? `?${s}` : ''}`;
}

function formatDate(iso: string, lang: string): string {
  return new Intl.DateTimeFormat(lang === 'de' ? 'de-DE' : 'en-GB', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'Europe/Berlin' }).format(new Date(iso));
}

function formatEur(cents: number, lang: string): string {
  return new Intl.NumberFormat(lang === 'de' ? 'de-DE' : 'en-GB', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

export function ServicePricesPage() {
  const { t } = useTranslation('service-prices');
  const [params, setParams] = useSearchParams();
  const pinned = params.get('version');
  const initialLang = params.get('lang');
  const [lang, setLang] = useState<'de' | 'en'>(initialLang === 'de' || initialLang === 'en' ? initialLang : currentLocale() === 'de' ? 'de' : 'en');
  const [data, setData] = useState<PublicServicePriceVersion | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [state, setState] = useState<'loading' | 'ok' | 'unpublished' | 'notfound' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState('loading');
      try {
        const q = new URLSearchParams({ lang, ...(pinned ? { version: pinned } : {}) });
        const r = await api<{ version: PublicServicePriceVersion }>(`/v1/portal/service-prices/${pinned ? 'version' : 'current'}?${q.toString()}`);
        if (cancelled) return;
        setData(r.version);
        setState('ok');
      } catch (err) {
        if (cancelled) return;
        setData(null);
        setState(err instanceof ApiError && err.status === 404 ? (pinned ? 'notfound' : 'unpublished') : 'error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lang, pinned]);

  useEffect(() => {
    let cancelled = false;
    api<{ items: HistoryEntry[] }>('/v1/portal/service-prices/versions')
      .then((r) => {
        if (!cancelled) setHistory(r.items);
      })
      .catch(() => {
        /* the history is a convenience; the version itself is what counts */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // the frozen text without its Markdown table (the table is rendered from the items below)
  const textBlocks = data ? data.content_md.split('\n').filter((l) => !l.trim().startsWith('|')).join('\n') : '';
  const [lead, ...rest] = textBlocks.split(/\n\s*\n/);

  return (
    <div className="min-h-dvh bg-gray-50 dark:bg-gray-800 px-4 py-10 text-gray-900 dark:text-gray-100">
      <div className="mx-auto w-full max-w-3xl rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-8 shadow-sm">
        <div className="mb-6 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <OrbotoLockup className="h-6 w-auto" />
          </Link>
          {data && (
            <span className="text-xs text-gray-500" data-testid="service-prices-version-badge">
              {t('versionBadge', { version: data.version, date: formatDate(data.effective_from, lang) })}
            </span>
          )}
        </div>

        {state === 'loading' && <p className="text-sm text-gray-500">{t('loading')}</p>}
        {state === 'unpublished' && (
          <p className="text-sm text-gray-600 dark:text-gray-400" data-testid="service-prices-unpublished">
            {t('unpublished')}
          </p>
        )}
        {state === 'notfound' && (
          <p className="text-sm text-gray-600 dark:text-gray-400" data-testid="service-prices-notfound">
            {t('notFound')}
          </p>
        )}
        {state === 'error' && (
          <p role="alert" className="text-sm text-red-700 dark:text-red-300">
            {t('error')}
          </p>
        )}

        {state === 'ok' && data && (
          <>
            {data.language !== 'de' && (
              <p className="mb-3 rounded border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-200" data-testid="service-prices-translation-note">
                {t('translationNote')}
              </p>
            )}
            <p className="mb-3 text-xs text-gray-500">
              <button type="button" onClick={() => setLang(data.language === 'de' ? 'en' : 'de')} className="underline" data-testid="service-prices-language-switch">
                {t('languageOf', { language: LOCALE_LABELS[data.language === 'de' ? 'en' : 'de'] })}
              </button>
            </p>
            {data.scheduled && (
              <p className="mb-3 text-sm text-amber-800 dark:text-amber-300" data-testid="service-prices-scheduled">
                {t('scheduled', { date: formatDate(data.effective_from, lang) })}
              </p>
            )}
            {!data.current && !data.scheduled && (
              <p className="mb-3 text-sm text-gray-600 dark:text-gray-400" data-testid="service-prices-superseded">
                {t('superseded')}{' '}
                <button type="button" onClick={() => setParams(new URLSearchParams({ lang }))} className="text-brand-500 underline">
                  {t('showCurrent')}
                </button>
              </p>
            )}
            <article className="text-sm leading-relaxed [&_p]:mt-2" data-testid="service-prices-body" lang={data.language}>
              <h1 className="text-xl font-semibold">{data.title}</h1>
              <ReactMarkdown>{lead ?? ''}</ReactMarkdown>
              <ReactMarkdown>{rest[0] ?? ''}</ReactMarkdown>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-left text-sm" data-testid="service-prices-table">
                  <thead className="text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="py-1 pr-3">{data.language === 'de' ? 'Leistung' : 'Service'}</th>
                      <th className="py-1 pr-3">{data.language === 'de' ? 'Einheit' : 'Unit'}</th>
                      <th className="py-1 pr-3 text-right">{data.language === 'de' ? 'Preis (netto)' : 'Price (net)'}</th>
                      <th className="py-1 pr-3">{data.language === 'de' ? 'Hinweis' : 'Note'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((i, idx) => (
                      <tr key={idx} className="border-t border-gray-100 dark:border-gray-800">
                        <td className="py-1.5 pr-3">{i.name}</td>
                        <td className="py-1.5 pr-3">{i.unit_label}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">{formatEur(i.net_amount_cents, data.language)}</td>
                        <td className="py-1.5 pr-3 text-gray-600 dark:text-gray-400">{i.note ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data.note && <p className="mt-4">{data.note}</p>}
              <p className="mt-8 break-all text-xs text-gray-500">{t('footer', { version: data.version, sha: data.content_sha256 })}</p>
            </article>
          </>
        )}

        {history.length > 1 && (
          <section className="mt-8 border-t border-gray-100 dark:border-gray-800 pt-4" data-testid="service-prices-history">
            <h2 className="text-sm font-semibold">{t('history')}</h2>
            <ul className="mt-2 text-sm">
              {history.map((h) => (
                <li key={h.version}>
                  {h.status === 'withdrawn' ? (
                    <span className="text-gray-500" data-testid="service-prices-history-withdrawn">
                      {t('historyEntry', { version: h.version, date: formatDate(h.effective_from, lang) })}
                      <span className="ml-2 text-xs">{t('historyWithdrawn')}</span>
                    </span>
                  ) : (
                    <Link to={servicePricesHref(h.version, lang)} className="text-brand-500 underline">
                      {t('historyEntry', { version: h.version, date: formatDate(h.effective_from, lang) })}
                    </Link>
                  )}
                  {h.current && <span className="ml-2 text-xs text-green-700 dark:text-green-300">{t('historyCurrent')}</span>}
                  {h.scheduled && <span className="ml-2 text-xs text-amber-700 dark:text-amber-300">{t('historyScheduled')}</span>}
                </li>
              ))}
            </ul>
          </section>
        )}
        <LegalFooter className="mt-8" />
      </div>
    </div>
  );
}
